<template>
  <div class="people-edit">
    <div v-if="isMobile && message.text.length > 0"
      class="people-edit__message"
      :class="{ [`people-edit__message--${message.type}`]: true }" >
      {{ message.text }}
    </div>
    <div class="people-edit__adults">
      <TextLabel class="people-edit__label" :label="adultsLabel" />
      <NumberInput class="people-edit__number-input"
        v-model="value.adults.value" :min="value.adults.min" :max="value.adults.max"
        @input="updateChildrenCount(); updateChildrenBirthdays();"/>
    </div>

    <div class="people-edit__children">
      <TextLabel class="people-edit__label" :label="childrenLabel" />
      <NumberInput class="people-edit__number-input" ref="child"
        v-model="value.children.value" :min="value.children.min" :max="maxChildren"
        @input="updateChildrenBirthdays"/>
    </div>

    <div class="people-edit__child-birthday" v-for="(birthday, index) in value.childrenBirthdays" :key="index">
      <TextLabel class="people-edit__birthday-label" :label="`Data urodzenia ${index + 1} dziecka`" />
      <DateInput class="people-edit__date-input" title="Wybierz datę urodzenia"
        v-model="value.childrenBirthdays[index]"
        :min="minChildAgeDate" :max="maxChildAgeDate"
        @input="setChildBirthdayCookie($event, index)"
        @toggle="$emit('dateInputToggled', $event)" />
      <div v-if="!isMobile" class="people-edit__birthday-icon">
        <Icon icon="cross" @click="removeChild(index)" />
      </div>
      <div v-if="lastUnchangedDateFieldIndex == index && !isMobile" class="people-edit__child-validation">
        <span class="people-edit__child-validation--label">{{ childrenValidationLabel }}</span>
        <p class="people-edit__child-validation--body">{{ childrenValidationBody }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import lastIndexOf from 'lodash/lastIndexOf'
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'

import DateInput from '@/components/DateInput.vue'
import Icon from '@/components/Icon.vue'
import NumberInput from '@/components/NumberInput.vue'
import ChildBirthdayAsCookie from '@/components/search/ChildBirthdayAsCookieMixin'
import TextLabel from '@/components/search/TextLabel.vue'
import MediaDetector from '@/utils/MediaDetector'

const INFANT = 1

@Component({
  mixins: [
    MediaDetector,
    ChildBirthdayAsCookie,
  ],
  components: {
    NumberInput,
    DateInput,
    TextLabel,
    Icon,
  },
  props: {
    value: {
      // {
      //   adults: { value: Number, min: Number, max: Number },
      //   children: { value: Number, min: Number, max: Number },
      //   childrenBirthdays: Array<moment>,
      // }
      type: Object,
      required: true,
    },
    adultsLabel: {
      type: String,
      required: false,
      default: () => 'Dorośli',
    },
    childrenLabel: {
      type: String,
      required: false,
      default: () => 'Dzieci',
    },
  },
  computed: {
    lastUnchangedDateFieldIndex () {
      const unchangedDates = this.value.childrenBirthdays.map(date => this.isNotValid(date))
      return lastIndexOf(unchangedDates, true)
    },
  },
})
export default class People extends Vue {
  childrenValidationLabel = 'Sprawdź datę urodzenia dziecka'
  childrenValidationBody = 'Określ dokładną datę urodzenia dziecka. Wiek dziecka wpływa na wyświetlaną cenę i ilość ofert'
  
  // Message displayed on top of the dialog
  get message () {
    if (this.isMaxAdults() && this.isMaxChildren()) {
      return {
        text: `Wybrany kierunek posiada pokoje do ${this.value.adults.max + this.maxChildren} OS. Łącznie`,
        type: 'error',
      }
    } else if (this.isMaxAdults()) {
      return {
        text: `Wybrany kierunek posiada pokoje do ${this.value.adults.max} OS. DOROSŁYCH`,
        type: 'error',
      }
    } else {
      return {
        text: '',
        type: 'error',
      }
    }
  }

  isMaxAdults () {
    return this.value.adults.value === this.value.adults.max
  }

  get maxChildren () {
    return Math.min(Math.max((this.value.adults.max - this.value.adults.value + INFANT), 0), this.value.children.max)
  }

  isMaxChildren () {
    return this.value.children.value === this.maxChildren
  }

  get minChildAgeDate () {
    return moment().subtract(16, 'years').startOf('day')
  }

  get maxChildAgeDate () {
    return moment().startOf('day')
  }

  updateChildrenCount () {
    this.value.children.value = Math.min(this.value.children.value, this.maxChildren)
  }

  updateChildrenBirthdays () {
    const childrenBirthdays = this.value.childrenBirthdays
    const date = this.getChildBirthdayCookie(childrenBirthdays.length)
    
    while (childrenBirthdays.length < this.value.children.value) {
      if (this.childBirthdayCookieExist(childrenBirthdays.length)) {
        childrenBirthdays.push(moment(date, 'DD.MM.YYYY'))
      } else {
        childrenBirthdays.push(moment().subtract(3, 'years'))
      }
    }

    while (childrenBirthdays.length > this.value.children.value) {
      childrenBirthdays.pop()
    }

    this.$emit('peopleCountValidation', this.message)
  }

  isNotValid (date) {
    const defaultDate = moment().subtract(3, 'years')
    return date.isSame(defaultDate, 'day')
  }

  clear () {
    this.value.adults.value = 2
    this.value.children.value = 0
    this.value.childrenBirthdays = []
  }

  removeChild (index) {
    this.value.childrenBirthdays.splice(index, 1)
    this.$refs.child.decrement()
  }
}
</script>

<style lang="scss" scoped>
.people-edit {
  display: flex;
  flex-direction: column;
  padding: 0;

  &__message {
    font-size: 12px;
    text-transform: uppercase;
    padding: 10px 20px 8px 20px;

    @include media(tablet) {
      padding: 6px 10px 6px 10px;
    }
  }

  &__message--error {
    color: white;
    background-color: #E71F6C;
  }

  &__label {
    color: #005CAA;
    font-family: 'Gotham';
    font-weight: 500;
    line-height: normal;
    font-size: 16px;
    text-transform: uppercase;
    margin-bottom: 6px;

    @include media(tablet) {
      margin-bottom: 0;
      font-size: 12px;
      padding: 9px 11px 3px;
    }
  }

  &__adults,
  &__children {
    margin: 0 auto;
  }

  &__adults {
    margin-top: 8px;

    @include media(tablet) {
      margin: 0;
    }
  }

  &__children {
    margin-top: 15px;
    margin-bottom: 20px;

    @include media(tablet) {
      margin: 0;

      .people-edit__label {
        padding: 12px 11px 3px;
      }
    }
  }

  &__number-input, 
  &__date-input {
    width:280px;

    @include media(desktop) {
      width: auto;
    }
    @include media(tablet) {
      width: auto;
    }
  }

  &__date-input {
    font-size: 16px;
    color: #545454;
  }

  &__child-birthday {
    max-width: 280px;
    margin: 0 auto 23px;
    position: relative;

    @include media(tablet) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-areas: "a a" "b c";
      padding: 5px 13px;
      border-bottom: 1px solid #EEEEEE;
    }

    div:first-child {
      grid-area: a;
    }

    div:nth-child(2) {
      grid-area: b;
    }

    div:last-child {
      grid-area: c;
    }

    &:last-child {
      margin-bottom: 0;
    }

    .figure {
      font-size: 9px;
      padding: 6px;
      width: 21px;
      margin-right: -8px;
      cursor: pointer;

      &:hover {
        color: #009FEA;
      }
    }
  }

  &__child-validation {
    position: absolute;
    width: 230px;
    right: 100%;
    bottom: -30px;
    margin-right: 94px;
    background-color: #fff;
    box-shadow: 0 0 15px -2px rgba(0, 0, 0, .3);
    padding: 4px 9px;

    &::after {
      content: '';
      position: absolute;
      width: 15px;
      height: 15px;
      top: 50%;
      right: -7px;
      transform: rotate(45deg);
      margin-top: -10px;
      background-color: #ffffff;
      box-shadow: 1px -1px 2px -1px rgba(0, 0, 0, 0.4);
    }

    &--label {
      color: #005CA9;
      font-size: 10px;
      font-weight: bold;
      font-family: 'Gotham';
      text-transform: uppercase;
    }

    &--body {
      margin: 3px 0;
      font-size: 11px;
      color: #535353;
    }
  }

  &__birthday-label {
    color: #005caa;
    text-transform: initial;
    font-size: 15px;
    font-weight: 300;
    margin-bottom: 5px;

    @include media(tablet) {
      font-size: 10px;
    }
  }

  &__birthday-icon {
    text-align: right;
    display: grid;
    justify-content: flex-end;
  }
}
</style>

<style lang="scss">
.people-edit__date-input .date-input__body {
  border: none;
}
</style>



// WEBPACK FOOTER //
// src/components/search/fields/PeopleEdit.vue